import EventTypes from "@wesstron/utils/Api/constants/eventTypes";
import moment from "moment";
import {
    Component,
    Suspense,
    useCallback,
    useMemo,
    useRef,
    useState,
} from "react";
import {Col, Row} from "react-bootstrap";
import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";
import {getQueryResults} from "../../../api/duckdb/getQueryResults";
import fetchData from "../../../api/fetch-suspense-wrapper/FetchSuspenseWrapper";
import {getResults} from "../../../api/results/getResults";
import EstimatedWeight from "../../../beans/estimated-weight/EstimatedWeight";
import {resultTargetList} from "../../../constans/resultSQLSelects";
import {UnitTypes} from "../../../constans/unitTypes";
import {
    getForageCostForDevice,
    getForageUsageForDevice,
    getGroupGaugesAggDataLoading,
    getWaterCostForDevice,
    getWaterUsageForDevice,
} from "../../../selectors/aggregatedDataSelector";
import {
    getAnimalEvents,
    getAnimalMedicineCost,
    getTechnologyGroupTransfers,
    getTransferEvents,
    makeGetWeightingEvents,
} from "../../../selectors/animalDocumentsSelectors";
import {
    getAllForages,
    getExpectedWaterUsageForGroup,
    getOtherWeightsSelector,
    getResultsTarget,
} from "../../../selectors/settingsSelector";
import {isService} from "../../../selectors/userSelector";
import {getPercent} from "../../../utils/GaugeUtils";
import {
    calculateFCRForClosedGroup,
    EstimatedWeightIndex,
    GROUP_ANIMAL_TYPES,
    sortWeightEvents,
} from "../../../utils/GroupUtils";
import {getTargetRange} from "../../../utils/ResultsV2Utils";
import {
    getAnimalUnit,
    getCalculatePigletGainOptions,
    getCalculatePorkerGainOptions,
    getFCRRange,
} from "../../../utils/SettingsUtils";
import {
    convertVolumeUnitTo,
    convertWeightUnitTo,
} from "../../../utils/UnitUtils";
import {createKeyToValueDictionary} from "../../../utils/Utils";
import Gauge from "../../basics/gauge/Gauge";
import InfoBox from "../../basics/info-box/InfoBox";
import LoadingComponent from "../../loading/LoadingComponent";
import CollapsableContainer from "../containers/CollapsableContainer";
import {applyOffset} from "../ideal-charts/charts/utils";
import Tooltip from "../../basics/tooltip/Tooltip";
import {CalculateGainTypes} from "../../../constans/gainOptions";
import {SectorType} from "../../../constans/sectorTypes";
import {first, last} from "lodash";
import SectorTypes from "@wesstron/utils/Api/constants/sectorTypes";
import {makeGetBuildingsMap} from "../../../selectors/buildingsSelector";
import {utcMomentToLocal} from "../../../utils/DateTimeUtils";
import {makeGetCurrency} from "../../../selectors/economySelectors";
import useDeviceLocations from "../ideal-charts/useDeviceLocations";
// FCR Error Codes for translation
const FCR_ERROR_CODES = {
    NO_FORAGE_USAGE: 1,
    NO_WEIGHT_GAIN: 2,
    NO_AVG_ANIMALS: 3,
    INVALID_FCR_VALUE: 4,
    NO_WEIGHTING_EVENTS: 5,
    NO_AVERAGE_ANIMALS_DATA: 6,
};

function GaugesContainer({hasMoreAnimalTypes, children, name}) {
    if (!hasMoreAnimalTypes)
        return (
            <Col lg={4} md={6} className="position-relative">
                {children}
            </Col>
        );
    return (
        <Col xs={12}>
            <fieldset className="fieldset mb-3 position-relative">
                <legend>{name}</legend>
                {children}
            </fieldset>
        </Col>
    );
}

function GaugeItem({
    label,
    min,
    max,
    value,
    valueFormatter,
    reverse,
    sideToSide,
}) {
    const percent = useMemo(() => {
        return getPercent(value, min, max);
    }, [value, min, max]);

    return (
        <Gauge
            label={label}
            min={min}
            max={max}
            value={value}
            percent={percent}
            valueFormatter={valueFormatter}
            reverse={reverse}
            sideToSide={sideToSide}
            showLegend
        />
    );
}
function MortalityGauges({
    groupTypes,
    hasMoreAnimalTypes,
    targets,
    targetDictionary,
    data,
}) {
    const {t} = useTranslation();

    const generalTarget = useMemo(() => {
        return getTargetRange("generalMortality", targets, targetDictionary);
    }, [targetDictionary, targets]);

    const weanerTarget = useMemo(() => {
        return getTargetRange("pigletMortality", targets, targetDictionary);
    }, [targetDictionary, targets]);

    const porkersTarget = useMemo(() => {
        return getTargetRange("pigletMortality", targets, targetDictionary);
    }, [targetDictionary, targets]);

    const giltTarget = useMemo(() => {
        return getTargetRange("pigletMortality", targets, targetDictionary);
    }, [targetDictionary, targets]);

    const mortalities = useMemo(() => {
        const tmp = {general: null, weaners: null, gilts: null, finisher: null};
        if (data.general.status === "SUCCESS") {
            tmp.general = data.general.data[0]?.mortality ?? null;
        }
        if (data.weaner?.status === "SUCCESS") {
            tmp.weaners = data.weaner.data[0]?.mortality ?? null;
        }
        if (data.gilts?.status === "SUCCESS") {
            tmp.gilts = data.gilts.data[0]?.mortality ?? null;
        }
        if (data.finisher?.status === "SUCCESS") {
            tmp.finisher = data.finisher.data[0]?.mortality ?? null;
        }
        return tmp;
    }, [data]);

    const colSizes = useMemo(() => {
        const elements =
            groupTypes.hasWeaners +
            groupTypes.hasGilts +
            groupTypes.hasFinisher;
        if (elements === 1) {
            return {lg: 12, md: 12};
        }
        let items = elements + 1;
        return {lg: 12 / items, md: items > 2 ? 6 : 12};
    }, [groupTypes]);

    const valueFormatter = useCallback((value) => value.toFixed(2) + "%", []);

    return (
        <GaugesContainer
            hasMoreAnimalTypes={hasMoreAnimalTypes}
            name={t("mortality")}>
            <Row>
                <Col {...colSizes}>
                    <GaugeItem
                        label={t("generalMortality")}
                        min={generalTarget[0]}
                        max={generalTarget[1]}
                        value={mortalities.general}
                        valueFormatter={valueFormatter}
                        reverse
                    />
                </Col>
                {groupTypes.hasWeaners && hasMoreAnimalTypes && (
                    <Col {...colSizes}>
                        <GaugeItem
                            label={t("animalTypes.2")}
                            min={weanerTarget[0]}
                            max={weanerTarget[1]}
                            value={mortalities.weaners}
                            valueFormatter={valueFormatter}
                            reverse
                        />
                    </Col>
                )}
                {groupTypes.hasFinisher && hasMoreAnimalTypes && (
                    <Col {...colSizes}>
                        <GaugeItem
                            label={t("animalTypes.3")}
                            min={porkersTarget[0]}
                            max={porkersTarget[1]}
                            value={mortalities.finisher}
                            valueFormatter={valueFormatter}
                            reverse
                        />
                    </Col>
                )}
                {groupTypes.hasGilts && hasMoreAnimalTypes && (
                    <Col {...colSizes}>
                        <GaugeItem
                            label={t("gilt")}
                            min={giltTarget[0]}
                            max={giltTarget[1]}
                            value={mortalities.gilts}
                            valueFormatter={valueFormatter}
                            reverse
                        />
                    </Col>
                )}
            </Row>
        </GaugesContainer>
    );
}

function SellGauges({
    hasMoreAnimalTypes,
    groupTypes,
    weights,
    targets,
    targetDictionary,
}) {
    const {t} = useTranslation();

    const generalTarget = useMemo(() => {
        return getTargetRange("generalSellWeight", targets, targetDictionary);
    }, [targetDictionary, targets]);

    const weanerTarget = useMemo(() => {
        return getTargetRange("pigletSellWeight", targets, targetDictionary);
    }, [targetDictionary, targets]);

    const porkersTarget = useMemo(() => {
        return getTargetRange("porkerSellWeight", targets, targetDictionary);
    }, [targetDictionary, targets]);

    const giltTarget = useMemo(() => {
        return getTargetRange("giltSellWeight", targets, targetDictionary);
    }, [targetDictionary, targets]);

    const colSizes = useMemo(() => {
        const elements =
            groupTypes.hasWeaners +
            groupTypes.hasGilts +
            groupTypes.hasFinisher;
        if (elements === 1) {
            return {lg: 12, md: 12};
        }
        let items = elements + 1;
        return {lg: 12 / items, md: items > 2 ? 6 : 12};
    }, [groupTypes]);

    const valueFormatter = useCallback((value) => {
        return convertWeightUnitTo(value, {
            unit: getAnimalUnit(),
            showUnit: true,
            fixed: 1,
        });
    }, []);

    return (
        <GaugesContainer
            hasMoreAnimalTypes={hasMoreAnimalTypes}
            name={t("averageSellWeight")}>
            <Row>
                <Col {...colSizes}>
                    <GaugeItem
                        label={t("generalAverageSellWeight")}
                        min={generalTarget[0]}
                        max={generalTarget[1]}
                        value={weights.general}
                        valueFormatter={valueFormatter}
                    />
                </Col>
                {groupTypes.hasWeaners && hasMoreAnimalTypes && (
                    <Col {...colSizes}>
                        <GaugeItem
                            label={t("animalTypes.2")}
                            min={weanerTarget[0]}
                            max={weanerTarget[1]}
                            value={weights.weaners}
                            valueFormatter={valueFormatter}
                        />
                    </Col>
                )}
                {groupTypes.hasFinisher && hasMoreAnimalTypes && (
                    <Col {...colSizes}>
                        <GaugeItem
                            label={t("animalTypes.3")}
                            min={porkersTarget[0]}
                            max={porkersTarget[1]}
                            value={weights.finisher}
                            valueFormatter={valueFormatter}
                        />
                    </Col>
                )}
                {groupTypes.hasGilts && hasMoreAnimalTypes && (
                    <Col {...colSizes}>
                        <GaugeItem
                            label={t("gilt")}
                            min={giltTarget[0]}
                            max={giltTarget[1]}
                            value={weights.gilts}
                            valueFormatter={valueFormatter}
                        />
                    </Col>
                )}
            </Row>
        </GaugesContainer>
    );
}

function SellGaugesWrapper({
    group,
    hasMoreAnimalTypes,
    groupTypes,
    targets,
    targetDictionary,
    forageLocations,
    groupInfo,
    inserted,
    data,
    loading,
}) {
    const weights = useMemo(() => {
        const tmp = {
            weaners: null,
            gilts: null,
            finisher: null,
            general: null,
        };
        if (data.average.general.status === "SUCCESS") {
            tmp.general = data.average.general.data[0]?.weight ?? null;
        }
        if (data.average.weaner?.status === "SUCCESS") {
            tmp.weaners = data.average.weaner.data[0]?.weight ?? null;
        }
        if (data.average.gilts?.status === "SUCCESS") {
            tmp.gilts = data.average.gilts.data[0]?.weight ?? null;
        }
        if (data.average.finisher?.status === "SUCCESS") {
            tmp.finisher = data.average.finisher.data[0]?.weight ?? null;
        }
        return tmp;
    }, [data]);

    return (
        <>
            <SellGauges
                groupTypes={groupTypes}
                hasMoreAnimalTypes={hasMoreAnimalTypes}
                targetDictionary={targetDictionary}
                targets={targets}
                weights={weights}
            />
            <SellWeightGauges
                group={group}
                groupTypes={groupTypes}
                hasMoreAnimalTypes={hasMoreAnimalTypes}
                targets={targets}
                targetDictionary={targetDictionary}
                data={data.total}
            />
            <ForageGauges
                group={group}
                hasMoreAnimalTypes={hasMoreAnimalTypes}
                groupInfo={groupInfo}
                forageLocations={forageLocations}
                inserted={inserted}
                weights={weights}
                loading={loading}
            />
        </>
    );
}

function GainToolTip({start, end}) {
    const {t} = useTranslation();
    const diff = moment(end)
        .startOf("day")
        .diff(moment(start).startOf("day"), "days");
    const tooltipContent = (
        <>
            <div>
                {t(
                    "shadowDescription.CL_SK4.climateCurve.stages.0.days._title"
                )}
                : {diff}
            </div>
            <div>
                {t("firstDate", {date: moment(start).format("DD.MM.YYYY")})}
            </div>
            <div>{t("lastDate", {date: moment(end).format("DD.MM.YYYY")})}</div>
        </>
    );
    if (!start || !end) return null;
    return (
        <Tooltip type="info" tooltipContent={tooltipContent}>
            <i
                style={{fontSize: "1.2rem"}}
                className="fa-solid fa-circle-info pointer info"
            />
        </Tooltip>
    );
}

function GainGauges({
    groupTypes,
    hasMoreAnimalTypes,
    targets,
    targetDictionary,
    data,
}) {
    const {t} = useTranslation();

    const PigletGainOptions = useSelector(getCalculatePigletGainOptions);

    const PorkerGainOptions = useSelector(getCalculatePorkerGainOptions);

    const isWeightGain = useMemo(() => {
        return {
            piglet:
                PigletGainOptions &&
                PigletGainOptions === CalculateGainTypes.WEIGHT,
            porker:
                PorkerGainOptions &&
                PorkerGainOptions === CalculateGainTypes.WEIGHT,
        };
    }, [PigletGainOptions, PorkerGainOptions]);

    const weanerTarget = useMemo(() => {
        return getTargetRange(
            isWeightGain.piglet ? "pigletGain" : "pigletSellGain",
            targets,
            targetDictionary
        );
    }, [targetDictionary, targets, isWeightGain]);

    const porkersTarget = useMemo(() => {
        return getTargetRange(
            isWeightGain.porker ? "porkerGain" : "porkerSellGain",
            targets,
            targetDictionary
        );
    }, [targetDictionary, targets, isWeightGain]);

    const gains = useMemo(() => {
        const tmp = {weaners: null, finisher: null};
        if (isWeightGain.piglet) {
            if (data.weanerWeightGain?.status === "SUCCESS") {
                tmp.weaners = data.weanerWeightGain.data[0]?.gain ?? null;
            }
            if (data.weanerLastWeighting?.status === "SUCCESS") {
                tmp.weanerLastWeighting = Number(
                    data.weanerLastWeighting.data?.[0]?.EventTime
                );
            }
            if (data.weanerFirstWeighting?.status === "SUCCESS") {
                tmp.weanerFirstWeighting = Number(
                    data.weanerFirstWeighting.data?.[0]?.EventTime
                );
            }
        } else {
            if (data.weanerSellGain?.status === "SUCCESS") {
                tmp.weaners = data.weanerSellGain.data[0]?.gain ?? null;
            }
            if (data.weanerSellDates?.status === "SUCCESS") {
                tmp.weanerLastWeighting = Number(
                    data.weanerSellDates.data?.[0]?.last
                );
            }
            if (data.weanerFirstWeighting?.status === "SUCCESS") {
                tmp.weanerFirstWeighting = Number(
                    data.weanerFirstWeighting.data?.[0]?.EventTime
                );
            }
        }

        if (isWeightGain.porker) {
            if (data.finisherWeightGain?.status === "SUCCESS") {
                tmp.finisher = data.finisherWeightGain.data[0]?.gain ?? null;
            }

            if (data.finisherFirstWeighting?.status === "SUCCESS") {
                tmp.finisherFirstWeighting = Number(
                    data.finisherFirstWeighting.data?.[0]?.EventTime
                );
            }
            if (data.finisherLastWeighting?.status === "SUCCESS") {
                tmp.finisherLastWeighting = Number(
                    data.finisherLastWeighting.data?.[0]?.EventTime
                );
            }
        } else {
            if (data.finisherSellGain?.status === "SUCCESS") {
                tmp.finisher = data.finisherSellGain.data[0]?.gain ?? null;
            }
            if (data.finisherSellDates?.status === "SUCCESS") {
                tmp.finisherLastWeighting = Number(
                    data.finisherSellDates.data?.[0]?.last
                );
            }
            if (data.finisherFirstWeighting?.status === "SUCCESS") {
                tmp.finisherFirstWeighting = Number(
                    data.finisherFirstWeighting.data?.[0]?.EventTime
                );
            }
        }
        if (data.generalSellGain?.status === "SUCCESS") {
            tmp.generalSellGain = data.generalSellGain.data[0]?.gain ?? null;
        }
        return tmp;
    }, [data, isWeightGain]);

    const colSizes = useMemo(() => {
        const elements = groupTypes.hasWeaners + groupTypes.hasFinisher;
        if (elements === 1) {
            return {lg: 12, md: 12};
        } else
            return {
                lg: 6,
                md: 6,
            };
    }, [groupTypes]);

    const valueFormatter = useCallback((value) => {
        return convertWeightUnitTo(value, {
            unit: UnitTypes.SMALL,
            showUnit: true,
            fixed: 0,
        });
    }, []);

    const weanerLabel = useMemo(() => {
        const start = gains.weanerFirstWeighting;
        const end = gains.weanerLastWeighting;
        const text = isWeightGain.piglet
            ? t("weanerGain")
            : t("weanerSellGain");
        return (
            <>
                {text}
                {gains.piglet > 0 && <GainToolTip start={start} end={end} />}
            </>
        );
    }, [gains, t, isWeightGain]);

    const porkerLabel = useMemo(() => {
        const start = gains.finisherFirstWeighting;
        const end = gains.finisherLastWeighting;
        const text = isWeightGain.porker
            ? t("finisherGain")
            : t("finisherSellGain");
        return (
            <>
                {text}
                {gains.finisher > 0 && <GainToolTip start={start} end={end} />}
            </>
        );
    }, [gains, t, isWeightGain]);

    if (!groupTypes.hasWeaners && !groupTypes.hasFinisher) return null;

    return (
        <GaugesContainer
            hasMoreAnimalTypes={hasMoreAnimalTypes}
            name={t("gain")}>
            <Row>
                {groupTypes.hasWeaners && (
                    <Col {...colSizes}>
                        <GaugeItem
                            label={weanerLabel}
                            min={weanerTarget[0]}
                            max={weanerTarget[1]}
                            value={gains.weaners > 0 ? gains.weaners : null}
                            valueFormatter={valueFormatter}
                        />
                    </Col>
                )}
                {groupTypes.hasFinisher && (
                    <Col {...colSizes}>
                        <GaugeItem
                            label={porkerLabel}
                            min={porkersTarget[0]}
                            max={porkersTarget[1]}
                            value={gains.finisher > 0 ? gains.finisher : null}
                            valueFormatter={valueFormatter}
                        />
                    </Col>
                )}
            </Row>
        </GaugesContainer>
    );
}

function SellWeightGauges({
    group,
    hasMoreAnimalTypes,
    queryCaller,
    groupTypes,
    targets,
    targetDictionary,
    data,
}) {
    const {t} = useTranslation();

    const generalTarget = useMemo(() => {
        return getTargetRange(
            "generalSellWeightTotal",
            targets,
            targetDictionary
        );
    }, [targetDictionary, targets]);

    const weanerTarget = useMemo(() => {
        return getTargetRange(
            "pigletSellWeightTotal",
            targets,
            targetDictionary
        );
    }, [targetDictionary, targets]);

    const porkersTarget = useMemo(() => {
        return getTargetRange(
            "porkerSellWeightTotal",
            targets,
            targetDictionary
        );
    }, [targetDictionary, targets]);

    const giltTarget = useMemo(() => {
        return getTargetRange("giltSellWeightTotal", targets, targetDictionary);
    }, [targetDictionary, targets]);

    const weights = useMemo(() => {
        const tmp = {
            weaners: null,
            gilts: null,
            finisher: null,
            general: null,
        };
        if (data.general?.status === "SUCCESS") {
            tmp.general = data.general.data[0]?.weight ?? null;
        }
        if (data.weaner?.status === "SUCCESS") {
            tmp.weaners = data.weaner.data[0]?.weight ?? null;
        }
        if (data.gilts?.status === "SUCCESS") {
            tmp.gilts = data.gilts.data[0]?.weight ?? null;
        }
        if (data.finisher?.status === "SUCCESS") {
            tmp.finisher = data.finisher.data[0]?.weight ?? null;
        }
        return tmp;
    }, [data]);

    const colSizes = useMemo(() => {
        const elements =
            groupTypes.hasWeaners +
            groupTypes.hasGilts +
            groupTypes.hasFinisher;
        if (elements === 1) {
            return {lg: 12, md: 12};
        }
        let items = elements + 1;
        return {lg: 12 / items, md: items > 2 ? 6 : 12};
    }, [groupTypes]);

    const valueFormatter = useCallback((value) => {
        if (value < 1000000) {
            return convertWeightUnitTo(value, {
                unit: UnitTypes.MEDIUM,
                showUnit: true,
                fixed: 1,
            });
        }
        return convertWeightUnitTo(value, {
            unit: UnitTypes.BIG,
            showUnit: true,
            fixed: 1,
        });
    }, []);

    return (
        <GaugesContainer
            hasMoreAnimalTypes={hasMoreAnimalTypes}
            name={t("sellWeight")}>
            <Row>
                <Col {...colSizes}>
                    <GaugeItem
                        label={t("generalSellWeight")}
                        min={generalTarget[0]}
                        max={generalTarget[1]}
                        value={weights.general}
                        valueFormatter={valueFormatter}
                    />
                </Col>
                {groupTypes.hasWeaners && hasMoreAnimalTypes && (
                    <Col {...colSizes}>
                        <GaugeItem
                            label={t("animalTypes.2")}
                            min={weanerTarget[0]}
                            max={weanerTarget[1]}
                            value={weights.weaners}
                            valueFormatter={valueFormatter}
                        />
                    </Col>
                )}
                {groupTypes.hasFinisher && hasMoreAnimalTypes && (
                    <Col {...colSizes}>
                        <GaugeItem
                            label={t("animalTypes.3")}
                            min={porkersTarget[0]}
                            max={porkersTarget[1]}
                            value={weights.finisher}
                            valueFormatter={valueFormatter}
                        />
                    </Col>
                )}
                {groupTypes.hasGilts && hasMoreAnimalTypes && (
                    <Col {...colSizes}>
                        <GaugeItem
                            label={t("gilt")}
                            min={giltTarget[0]}
                            max={giltTarget[1]}
                            value={weights.gilts}
                            valueFormatter={valueFormatter}
                        />
                    </Col>
                )}
            </Row>
        </GaugesContainer>
    );
}

function ForageGauges({
    group,
    groupInfo,
    forageLocations,
    weights,
    inserted,
    loading: loadingProps,
}) {
    const {t} = useTranslation();

    const fcrRange = useRef(getFCRRange());

    const getBuildingsMap = useMemo(makeGetBuildingsMap, []);

    const allEvents = useSelector(getAnimalEvents);

    const pigletReclassifyTime = useMemo(() => {
        let minTime = null;

        for (const e of allEvents) {
            if (e.EvCode === EventTypes.PIGLET_RECLASSIFY) {
                if (minTime === null || e.EvTime < minTime) {
                    minTime = e.EvTime;
                }
            }
        }

        return minTime;
    }, [allEvents]);

    const buildingsMap = useSelector(getBuildingsMap);

    const forageUsage = useSelector((state) =>
        getForageUsageForDevice(
            state,
            groupInfo.start,
            groupInfo.end,
            forageLocations,
            groupInfo
        )
    );
    const allForages = useSelector(getAllForages);
    const fUsage = useMemo(() => {
        if (!forageUsage) return null;
        const tmp = [];
        const foragesUsage = Object.values(forageUsage.forages);
        for (let row of foragesUsage) {
            if (row.forageID) {
                console.log(row);
                const forageName =
                    allForages.find((item) => item.SetID === row.forageID)
                        ?.SetData?.Name || "unknown";
                const min = applyOffset(row.expected, -row.offset);
                const max = applyOffset(row.expected, row.offset);
                const minAll = applyOffset(row.expectedAll, -row.offset);
                const maxAll = applyOffset(row.expectedAll, row.offset);
                console.log(forageName, min, max, minAll, maxAll);
                tmp.push(
                    {
                        label:
                            forageName === "unknown"
                                ? t("forageDispensedUnknown")
                                : t("forageDispensedName", {name: forageName}),
                        value: row.usage,
                        min: minAll,
                        max: maxAll,
                        id: row.forageID,
                    },
                    {
                        label:
                            forageName === "unknown"
                                ? t("forageDispensedForPieceUnknown")
                                : t("forageDispensedForPieceName", {
                                      name: forageName,
                                  }),
                        value: row.forPiece,
                        min,
                        max,
                        id: row.forageID + "Piece",
                    }
                );
            }
        }
        return tmp;
    }, [forageUsage, allForages, t]);

    const weightArray = useSelector(getOtherWeightsSelector);

    const averageAnimalsInserted = useMemo(() => {
        if (!groupInfo) return null;

        const estimate = new EstimatedWeight(0, []);
        estimate.setWeightTable(weightArray, 1200);
        estimate.setBirthDate(groupInfo.birthTime);

        const numberOfAnimals = {};
        const estimatedWeight = {};
        const avgAnmCnt = {};

        const diff = Math.abs(
            moment.utc(groupInfo.start).diff(moment.utc(groupInfo.end), "day")
        );

        let sum = 0;
        const sumGrouped = {
            [SectorTypes.PORK_HOUSE]: 0,
            [SectorTypes.PIGLET_HOUSE]: 0,
        };
        const days = {
            [SectorTypes.PORK_HOUSE]: 0,
            [SectorTypes.PIGLET_HOUSE]: 0,
        };

        let porkerStart = {};

        for (let i = 0; i <= diff; i++) {
            const dayUTC = moment.utc(groupInfo.start).add(i, "day");
            const dayLOCAL = utcMomentToLocal(dayUTC);
            const estimated = estimate.getWeightByDate(+dayLOCAL);

            let daySum = 0;

            for (const locationId of Object.keys(
                groupInfo.animalNumberPerLocation
            )) {
                const location = buildingsMap.get(locationId);
                const isPigletHouse =
                    location?.SType === SectorTypes.PIGLET_HOUSE;

                const animals = groupInfo.getNumberByTimeAndLocation(
                    +dayLOCAL,
                    locationId
                );

                if (!animals) continue;

                daySum += animals;
                sum += animals;

                const key = isPigletHouse
                    ? SectorTypes.PIGLET_HOUSE
                    : SectorTypes.PORK_HOUSE;

                sumGrouped[key] += animals;
                days[key]++;

                if (
                    !isPigletHouse &&
                    (!porkerStart.Time ||
                        (+dayLOCAL === porkerStart.Time &&
                            daySum > porkerStart.Amount))
                ) {
                    porkerStart = {
                        Amount: daySum,
                        Weight: estimated,
                        Time: +dayLOCAL,
                    };
                }
            }

            numberOfAnimals[+dayUTC] = daySum;
            estimatedWeight[+dayUTC] = estimated;
            avgAnmCnt[+dayUTC] = Math.round((sum / (i + 1)) * 100) / 100;
        }

        const estimatedWeightIndex = new EstimatedWeightIndex(estimatedWeight);

        return {
            avg: {
                all: sum / (diff + 1),
                [SectorTypes.PIGLET_HOUSE]:
                    sumGrouped[SectorTypes.PIGLET_HOUSE] /
                    (days[SectorTypes.PIGLET_HOUSE] + 1),
                [SectorTypes.PORK_HOUSE]:
                    sumGrouped[SectorTypes.PORK_HOUSE] /
                    (days[SectorTypes.PORK_HOUSE] + 1),
            },
            numberOfAnimals,
            porkerStart,
            avgAnmCnt,
            estimatedWeight,
            estimatedWeightIndex,
        };
    }, [groupInfo, weightArray, buildingsMap]);

    const [forageUsageForSector, setForageUsageForSector] = useState(null);

    const fPlcmntUsage = useMemo(() => {
        if (!forageUsage) return null;
        const usageForSType = {};
        let tmp = [];
        const obj = {};
        for (let PlcmntID in forageUsage.plcmnt) {
            const location = buildingsMap.get(PlcmntID);
            if (!usageForSType[location.SType])
                usageForSType[location.SType] = {
                    all: 0,
                    forPiece: 0,
                    expected: {min: 0, max: 0},
                    expectedAll: {min: 0, max: 0},
                };
            usageForSType[location.SType].all +=
                forageUsage.plcmnt[PlcmntID].usage;
            usageForSType[location.SType].forPiece +=
                forageUsage.plcmnt[PlcmntID].forPiece;
            usageForSType[location.SType].expected.min += applyOffset(
                forageUsage.plcmnt[PlcmntID].expected,
                -forageUsage.plcmnt[PlcmntID].offset
            );
            usageForSType[location.SType].expected.max += applyOffset(
                forageUsage.plcmnt[PlcmntID].expected,
                forageUsage.plcmnt[PlcmntID].offset
            );
            usageForSType[location.SType].expectedAll.min += applyOffset(
                forageUsage.plcmnt[PlcmntID].expectedAll,
                -forageUsage.plcmnt[PlcmntID].offset
            );
            usageForSType[location.SType].expectedAll.max += applyOffset(
                forageUsage.plcmnt[PlcmntID].expectedAll,
                forageUsage.plcmnt[PlcmntID].offset
            );
        }
        for (let SType in usageForSType) {
            const usage = usageForSType[SType];
            obj[SType] = convertWeightUnitTo(usage.all, {
                unit: UnitTypes.MEDIUM,
            });
            tmp.push(
                {
                    label: `${t("forageDispensed")} - ${t(
                        `SType.${SType - 1}`
                    )}`,
                    value: usage.all,
                    min: usage.expectedAll.min,
                    max: usage.expectedAll.max,
                },
                {
                    label: `${t("forageDispensedForPiece")} - ${t(
                        `SType.${SType - 1}`
                    )}`,
                    value: usage.forPiece,
                    min: usage.expected.min,
                    max: usage.expected.max,
                }
            );
        }
        setForageUsageForSector(obj);
        return tmp;
    }, [forageUsage, buildingsMap, t]);

    const _transfers = useSelector(getTransferEvents);
    const _technologyGroupTransfers = useSelector(getTechnologyGroupTransfers);

    const forageKeys = useMemo(() => {
        return forageUsageForSector ? Object.keys(forageUsageForSector) : [];
    }, [forageUsageForSector]);

    const transfers = useMemo(() => {
        const allTransfers = [..._transfers, ..._technologyGroupTransfers];
        const obj = {
            [SectorType.PIGLET_HOUSE]: [],
            [SectorType.PORK_HOUSE]: [],
            pigletToPorkHouse: [],
        };
        const isTrasferToPorkHouse = (event) => {
            if (
                event.EvCode === EventTypes.TRANSFER &&
                event.EvData.DstID &&
                event.EvData.SrcID
            ) {
                const pigletLoc = buildingsMap.get(event.EvData.SrcID);
                const porkerLoc = buildingsMap.get(event.EvData.DstID);
                if (
                    pigletLoc &&
                    porkerLoc &&
                    pigletLoc.SType === SectorTypes.PIGLET_HOUSE &&
                    porkerLoc.SType === SectorTypes.PORK_HOUSE
                )
                    return true;
            }
        };

        const hasSingleSector = forageKeys.length === 1;
        if (hasSingleSector)
            console.log(
                `[FCR - ${group.AnmGrp}] Single sector detected (${forageKeys[0]}), assigning all transfer events to it`
            );
        if (!pigletReclassifyTime && hasSingleSector) {
            const sectorKey = Number(forageKeys[0]);
            obj[sectorKey].push(...allTransfers);
        } else {
            for (const event of allTransfers) {
                if (isTrasferToPorkHouse(event)) {
                    obj.pigletToPorkHouse.push(event);
                } else if (event.EvTime > pigletReclassifyTime) {
                    obj[SectorType.PORK_HOUSE].push(event);
                } else {
                    obj[SectorType.PIGLET_HOUSE].push(event);
                }
            }
        }

        return obj;
    }, [
        _transfers,
        _technologyGroupTransfers,
        pigletReclassifyTime,
        group.AnmGrp,
        buildingsMap,
        forageKeys,
    ]);

    const props = useRef({getDeletedAnimals: true});
    const getWeightingEvents = useMemo(() => makeGetWeightingEvents(), []);
    const weightingEvents = useSelector((state) =>
        getWeightingEvents(state, props.current)
    );

    const groupedWeightingEvents = useMemo(() => {
        const obj = {
            [SectorType.PIGLET_HOUSE]: [],
            [SectorType.PORK_HOUSE]: [],
        };
        const tmp = sortWeightEvents(weightingEvents);
        // Check if forageUsageForSector has only one key
        const hasSingleSector = forageKeys.length === 1;

        if (hasSingleSector)
            console.log(
                `[FCR - ${group.AnmGrp}] Single sector detected (${forageKeys[0]}), assigning all transfer events to it`
            );
        if (!pigletReclassifyTime && hasSingleSector) {
            const sectorKey = Number(forageKeys[0]);
            obj[sectorKey] = tmp;
        } else {
            for (const event of tmp) {
                if (event.EvTime > pigletReclassifyTime) {
                    obj[SectorType.PORK_HOUSE].push(event);
                } else {
                    obj[SectorType.PIGLET_HOUSE].push(event);
                }
            }
        }

        return obj;
    }, [weightingEvents, pigletReclassifyTime, group.AnmGrp, forageKeys]);

    const hasPorkSector = useMemo(
        () => forageKeys?.includes(`${SectorType.PORK_HOUSE}`),
        [forageKeys]
    );

    const hasPigletSector = useMemo(
        () => forageKeys?.includes(`${SectorType.PIGLET_HOUSE}`),
        [forageKeys]
    );

    const totalWeightGain = useMemo(() => {
        const obj = {
            [SectorType.PIGLET_HOUSE]: null,
            [SectorType.PORK_HOUSE]: null,
        };
        if (!weightingEvents?.length) return null;

        const pigletEvents = [];
        const porkerEvents = [];

        // If only one sector exists, assign all events to that sector
        if (hasPigletSector && !hasPorkSector) {
            pigletEvents.push(
                ...weightingEvents.filter(
                    (e) => e.EvCode === EventTypes.WEIGHTING
                )
            );
        } else if (hasPorkSector && !hasPigletSector) {
            porkerEvents.push(
                ...weightingEvents.filter(
                    (e) => e.EvCode === EventTypes.WEIGHTING
                )
            );
        } else {
            // Both sectors exist - split by pigletReclassifyTime
            for (const event of weightingEvents) {
                if (event.EvCode === EventTypes.WEIGHTING) {
                    if (event.EvTime > pigletReclassifyTime) {
                        porkerEvents.push(event);
                    } else {
                        pigletEvents.push(event);
                    }
                }
            }
        }

        const sortedPigletEvents = sortWeightEvents(pigletEvents);
        const sortedPorkerEvents = sortWeightEvents(porkerEvents);

        if (hasPigletSector && sortedPigletEvents.length) {
            const pigletGain =
                last(sortedPigletEvents)?.EvData.Weight - group.Weight;
            obj[SectorType.PIGLET_HOUSE] = convertWeightUnitTo(pigletGain, {
                unit: UnitTypes.MEDIUM,
            });
        }

        if (hasPorkSector && sortedPorkerEvents.length) {
            const weightGain =
                last(sortedPorkerEvents).EvData.Weight -
                first(sortedPorkerEvents).EvData.Weight;
            obj[SectorType.PORK_HOUSE] = convertWeightUnitTo(weightGain, {
                unit: UnitTypes.MEDIUM,
            });
        }

        console.log(`[FCR - ${group.AnmGrp}] - totalWeightGain`, obj);
        return obj;
    }, [
        weightingEvents,
        group,
        pigletReclassifyTime,
        hasPorkSector,
        hasPigletSector,
    ]);

    const isFcrValid = useCallback((fcr) => {
        return fcr > 1.7 && fcr < 4.5;
    }, []);

    const fcr = useMemo(() => {
        const tmp = {};
        if (hasPigletSector) {
            tmp[SectorType.PIGLET_HOUSE] = {
                open: {value: null, error: true, errorCode: null},
                close: {value: null, error: true, errorCode: null},
            };
        }
        if (hasPorkSector) {
            tmp[SectorType.PORK_HOUSE] = {
                open: {value: null, error: true, errorCode: null},
                close: {value: null, error: true, errorCode: null},
            };
        }
        console.log("[FCR] Animals: ", tmp);
        for (let key in tmp) {
            const forageUsage = forageUsageForSector?.[key];
            const weightGain = totalWeightGain?.[key];
            const avgAnimals = averageAnimalsInserted.avg[key];
            console.log(
                `[FCR - ${group.AnmGrp}] (OTWARTA) (${t(`SType.${key - 1}`)}) - Łączne zużycie paszy[kg]: ${forageUsage}, Średnia liczba zwierząt: ${avgAnimals}, Przyrost wagi[kg]: ${weightGain}`
            );
            //LICZENIE DLA OTWARTEJ GRUPY
            if (!forageUsage) {
                tmp[key].open = {
                    value: null,
                    error: true,
                    errorCode: FCR_ERROR_CODES.NO_FORAGE_USAGE,
                };
            } else if (!weightGain || !Number(weightGain)) {
                tmp[key].open = {
                    value: null,
                    error: true,
                    errorCode: FCR_ERROR_CODES.NO_WEIGHT_GAIN,
                };
            } else if (!avgAnimals) {
                tmp[key].open = {
                    value: null,
                    error: true,
                    errorCode: FCR_ERROR_CODES.NO_AVG_ANIMALS,
                };
            } else {
                const fcr = forageUsage / (avgAnimals * weightGain);
                const isValid = isFcrValid(fcr);
                tmp[key].open = {
                    value: fcr,
                    error: !isValid,
                    errorCode: !isValid
                        ? FCR_ERROR_CODES.INVALID_FCR_VALUE
                        : null,
                };
            }

            const currentTransfers = transfers?.[key];
            const pigletToPorkhouse = transfers?.pigletToPorkHouse;
            const weightings = groupedWeightingEvents?.[key];

            if (!weightings?.length) {
                tmp[key].close = {
                    value: null,
                    error: true,
                    errorCode: FCR_ERROR_CODES.NO_WEIGHTING_EVENTS,
                };
            } else if (!forageUsage) {
                tmp[key].close = {
                    value: null,
                    error: true,
                    errorCode: FCR_ERROR_CODES.NO_FORAGE_USAGE,
                };
            } else if (!averageAnimalsInserted) {
                tmp[key].close = {
                    value: null,
                    error: true,
                    errorCode: FCR_ERROR_CODES.NO_AVERAGE_ANIMALS_DATA,
                };
            } else {
                const fcrForClosedGroup = calculateFCRForClosedGroup(
                    group,
                    currentTransfers,
                    forageUsage,
                    weightings,
                    averageAnimalsInserted,
                    buildingsMap,
                    Number(key) === SectorTypes.PORK_HOUSE,
                    pigletReclassifyTime,
                    pigletToPorkhouse
                );
                const isValid = isFcrValid(fcrForClosedGroup);
                tmp[key].close = {
                    value: fcrForClosedGroup,
                    error: !isValid,
                    errorCode: !isValid
                        ? FCR_ERROR_CODES.INVALID_FCR_VALUE
                        : null,
                };
            }
            //LICZENIE DLA ZAMKNIĘTEJ GRUPY
        }
        return tmp;
    }, [
        group,
        forageUsageForSector,
        averageAnimalsInserted,
        totalWeightGain,
        transfers,
        isFcrValid,
        groupedWeightingEvents,
        t,
        pigletReclassifyTime,
        buildingsMap,
        hasPorkSector,
        hasPigletSector,
    ]);
    console.log("[FCR - Podsumowanie]", fcr);
    const valueFormatter = useCallback((value) => {
        const unit = value > 10000000 ? UnitTypes.BIG : UnitTypes.MEDIUM;
        return convertWeightUnitTo(value, {
            unit: unit,
            showUnit: true,
            fixed: 1,
        });
    }, []);
    const isServiceUser = useSelector(isService);
    if (!inserted) return null;
    const isClosedGroup = !!(group.DtaDltTime || !group.AnmIDs.length);

    const FCRToolTip = ({animalType, error, errorCode, isService}) => {
        const {t} = useTranslation();
        const tooltipContent = useMemo(
            () => (isService ? t(`fcrError.${errorCode}`) : t("fcrError")),
            [isService, errorCode, t]
        );
        return (
            <>
                {t(
                    `animalTypes.${Number(animalType) === SectorTypes.PIGLET_HOUSE ? 2 : 3}`
                )}
                {error && (
                    <Tooltip type="error" tooltipContent={tooltipContent}>
                        <i
                            style={{fontSize: "1.2rem"}}
                            className="ms-1 fa-solid fa-circle-info pointer error"
                        />
                    </Tooltip>
                )}
            </>
        );
    };

    if (!hasPigletSector && !hasPorkSector) return null;
    return (
        <>
            <GaugesContainer hasMoreAnimalTypes name={t("fcr")}>
                <LoadingComponent isLoading={loadingProps} />
                <Row>
                    {Object.entries(fcr)
                        .reverse()
                        .map(([key, item]) => {
                            const obj = isClosedGroup
                                ? item?.close
                                : item?.open;
                            const value = obj.error
                                ? isServiceUser
                                    ? obj.value
                                    : null
                                : obj.value;
                            return (
                                <Col
                                    xl={12 / Object.keys(fcr).length}
                                    className="position-relative"
                                    key={key}>
                                    <GaugeItem
                                        label={
                                            <FCRToolTip
                                                animalType={key}
                                                error={obj.error}
                                                errorCode={obj.errorCode}
                                                isService={isService}
                                            />
                                        }
                                        min={fcrRange.current[0]}
                                        max={fcrRange.current[1]}
                                        value={value}
                                        reverse
                                    />
                                </Col>
                            );
                        })}
                </Row>
            </GaugesContainer>

            {fUsage && fUsage.length > 0 && (
                <GaugesContainer
                    hasMoreAnimalTypes
                    name={t("IOT.forageConsumption")}>
                    <LoadingComponent isLoading={loadingProps} />
                    <Row className="justify-content-center">
                        {fUsage.map((row) => (
                            <Col key={row.id} lg={3} md={6}>
                                <GaugeItem
                                    label={row.label}
                                    min={row.min}
                                    max={row.max}
                                    value={row.value}
                                    valueFormatter={valueFormatter}
                                    sideToSide
                                />
                            </Col>
                        ))}
                    </Row>
                </GaugesContainer>
            )}
            {fPlcmntUsage && fPlcmntUsage.length > 0 && (
                <GaugesContainer
                    hasMoreAnimalTypes
                    name={t("forageUsageForSector")}>
                    <LoadingComponent isLoading={loadingProps} />
                    <Row className="justify-content-center">
                        {fPlcmntUsage.map((row) => (
                            <Col key={row.id} lg={3} md={6}>
                                <GaugeItem
                                    label={row.label}
                                    min={row.min}
                                    max={row.max}
                                    value={row.value}
                                    valueFormatter={valueFormatter}
                                    sideToSide
                                />
                            </Col>
                        ))}
                    </Row>
                </GaugesContainer>
            )}
        </>
    );
}

function WaterGauges({groupInfo, waterLocations, inserted, loading}) {
    const {t} = useTranslation();

    const waterUsage = useSelector((state) =>
        getWaterUsageForDevice(
            state,
            groupInfo.start,
            groupInfo.end,
            waterLocations,
            groupInfo
        )
    );

    const expectedWaterUsage = useSelector((state) =>
        getExpectedWaterUsageForGroup(state, groupInfo)
    );

    const wUsage = useMemo(() => {
        if (!waterUsage) return null;
        return [
            {
                label: t("dispensedWater"),
                value: waterUsage?.usage,
                min: expectedWaterUsage.min * inserted,
                max: expectedWaterUsage.max * inserted,
                id: "waterPiece",
            },
            {
                label: t("dispensedWaterForPiece"),
                value: waterUsage?.forPiece,
                ...expectedWaterUsage,
                id: "water",
            },
        ];
    }, [waterUsage, inserted, t, expectedWaterUsage]);

    const valueFormatter = useCallback((value) => {
        const unit = value > 10000000 ? UnitTypes.BIG : UnitTypes.MEDIUM;
        return convertVolumeUnitTo(value, {
            showUnit: true,
            unit: unit,
            fixed: 1,
        });
    }, []);

    if (!wUsage) return null;
    return (
        <GaugesContainer
            hasMoreAnimalTypes
            name={t("charts.waterHourlyChart.consumption")}>
            <LoadingComponent isLoading={loading} />
            <Row className="justify-content-center">
                {wUsage.map((row) => (
                    <Col key={row.id} md={6}>
                        <GaugeItem
                            key={row.label}
                            label={row.label}
                            min={row.min}
                            max={row.max}
                            value={row.value}
                            sideToSide
                            valueFormatter={valueFormatter}
                        />
                    </Col>
                ))}
            </Row>
        </GaugesContainer>
    );
}

function EconomyItem({label, value, valueFormatter}) {
    return (
        <Col
            xs={12}
            lg={3}
            className="d-flex justify-content-center align-items-center flex-column mb-2">
            <h3 className="mb-1">
                {valueFormatter ? valueFormatter(value) : value}
            </h3>
            <div>{label}</div>
        </Col>
    );
}

function EconomyData({
    groupInfo,
    forageLocations,
    waterLocations,
    groupResults,
    inserted,
}) {
    const {t} = useTranslation();

    const getCurrency = useMemo(makeGetCurrency, []);
    const currency = useSelector(getCurrency);

    const forageCost = useSelector((state) =>
        getForageCostForDevice(
            state,
            groupInfo.start,
            groupInfo.end,
            forageLocations
        )
    );
    const waterCost = useSelector((state) =>
        getWaterCostForDevice(
            state,
            groupInfo.start,
            groupInfo.end,
            waterLocations
        )
    );
    const medicineCost = useSelector(getAnimalMedicineCost);

    const currencyFormatter = useCallback(
        (value) => value.toFixed(2) + " " + currency,
        [currency]
    );

    const data = useMemo(() => {
        if (!groupResults) return [];

        return [
            {
                label: t("forageCost"),
                valueAll: forageCost,
                valueAnimal: forageCost / inserted,
                show: !!forageCost,
                valueFormatter: currencyFormatter,
            },
            {
                label: t("waterCost"),
                valueAll: waterCost,
                valueAnimal: waterCost / inserted,
                show: !!waterCost,
                valueFormatter: currencyFormatter,
            },
            {
                label: t("medicationCost"),
                valueAll: medicineCost,
                valueAnimal: medicineCost / inserted,
                show: !!medicineCost,
                valueFormatter: currencyFormatter,
            },
            // {
            //     label: t("fixedCost"),
            //     valueAll: fixedCost,
            //     valueAnimal: fixedCost / inserted,
            //     show: !!fixedCost,
            //     text: true,
            //     valueFormatter: currencyFormatter
            // },
            // {
            //     label: t("buyCost"),
            //     valueAll: buyCost,
            //     valueAnimal: buyCost / inserted,
            //     show: !!buyCost,
            //     text: true,
            //     valueFormatter: currencyFormatter
            // }
        ].filter((item) => item.show);
    }, [
        forageCost,
        groupResults,
        waterCost,
        medicineCost,
        currencyFormatter,
        t,
        inserted,
    ]);

    if (data.length === 0) return null;

    return (
        <CollapsableContainer.Card
            className="economy-data"
            header={t("economy")}
            defaultExpanded>
            <fieldset className="fieldset mb-3">
                <legend>{t("total")}</legend>
                <Row>
                    {data.map(({label, valueAll, valueFormatter}, index) => (
                        <EconomyItem
                            label={label}
                            value={valueAll}
                            valueFormatter={valueFormatter}
                            key={index}
                        />
                    ))}
                </Row>
            </fieldset>
            <fieldset className="fieldset">
                <legend>{t("forPiece")}</legend>
                <Row>
                    {data.map(({label, valueAnimal, valueFormatter}, index) => (
                        <EconomyItem
                            label={label}
                            value={valueAnimal}
                            valueFormatter={valueFormatter}
                            key={index}
                        />
                    ))}
                </Row>
            </fieldset>
        </CollapsableContainer.Card>
    );
}

function GroupGaugesRendererRow({
    group,
    groupTypes,
    forageLocations,
    groupInfo,
    inserted,
    waterLocations,
    resource,
    loading,
}) {
    const data = resource.read();
    const groupedData = useMemo(() => {
        const mortality = {
            general: data.generalMortality,
            weaner: data.pigletMortality,
            gilts: data.giltMortality,
            finisher: data.porkerMortality,
        };

        const gain = {
            weanerWeightGain: data.pigletGain,
            weanerSellGain: data.pigletSellGain,
            weanerFirstWeighting: data.pigletFirstWeighting,
            weanerLastWeighting: data.pigletLastWeighting,
            weanerSellDates: data.pigletSellDates,
            finisherWeightGain: data.porkerGain,
            finisherSellGain: data.porkerSellGain,
            finisherFirstWeighting: data.porkerFirstWeighting,
            finisherLastWeighting: data.porkerLastWeighting,
            finisherSellDates: data.porkerSellDates,
            generalSellGain: data.generalSellGain,
        };
        const sell = {
            average: {
                general: data.generalSellWeight,
                weaner: data.pigletSellWeight,
                gilts: data.giltSellWeight,
                finisher: data.porkerSellWeight,
            },
            total: {
                general: data.generalSellWeightTotal,
                weaner: data.pigletSellWeightTotal,
                gilts: data.giltSellWeightTotal,
                finisher: data.porkerSellWeightTotal,
            },
        };

        return {mortality, gain, sell};
    }, [data]);

    const allTargets = useSelector(getResultsTarget);

    const targets = useMemo(() => allTargets?.porker, [allTargets]);

    const targetDictionary = useMemo(() => {
        return createKeyToValueDictionary(resultTargetList, "sqlKey");
    }, []);

    const hasMoreAnimalTypes = useMemo(() => {
        return (
            groupTypes.hasWeaners +
                groupTypes.hasGilts +
                groupTypes.hasFinisher >
            1
        );
    }, [groupTypes]);

    return (
        <Row>
            <MortalityGauges
                groupTypes={groupTypes}
                hasMoreAnimalTypes={hasMoreAnimalTypes}
                targets={targets}
                targetDictionary={targetDictionary}
                data={groupedData.mortality}
            />
            <GainGauges
                groupTypes={groupTypes}
                hasMoreAnimalTypes={hasMoreAnimalTypes}
                targets={targets}
                targetDictionary={targetDictionary}
                data={groupedData.gain}
            />
            <SellGaugesWrapper
                group={group}
                groupTypes={groupTypes}
                hasMoreAnimalTypes={hasMoreAnimalTypes}
                targets={targets}
                targetDictionary={targetDictionary}
                forageLocations={forageLocations}
                groupInfo={groupInfo}
                inserted={inserted}
                data={groupedData.sell}
                loading={loading}
            />
            <WaterGauges
                groupInfo={groupInfo}
                waterLocations={waterLocations}
                inserted={inserted}
                loading={loading}
            />
        </Row>
    );
}

function GroupGaugesLoader() {
    const {t} = useTranslation();

    return (
        <CollapsableContainer.Card
            header={t("results.results")}
            defaultExpanded>
            <div style={{height: "15rem"}} className="position-relative">
                <LoadingComponent isLoading />
            </div>
        </CollapsableContainer.Card>
    );
}

function GroupGaugesRenderer({
    resource,
    group,
    forageLocations,
    groupInfo,
    waterLocations,
    loading,
}) {
    const data = resource.read();
    const {t} = useTranslation();

    const {groupTypes, inserted} = useMemo(() => {
        const defaultGroupTypes = {
            hasWeaners: false,
            hasGilts: false,
            hasFinisher: false,
        };
        let groupTypes =
            data.groupAnimalTypes.ExecCode === "SUCCEEDED"
                ? data.groupAnimalTypes.Data[0]
                : defaultGroupTypes;
        const inserted =
            data.generalInserted.status === "SUCCESS"
                ? data.generalInserted.data[0]?.amount
                : 0;
        return {groupTypes, inserted};
    }, [data]);
    const PigletGainOptions = useSelector(getCalculatePigletGainOptions);

    const PorkerGainOptions = useSelector(getCalculatePorkerGainOptions);

    const isWeightGain = useMemo(() => {
        return {
            piglet:
                PigletGainOptions &&
                PigletGainOptions === CalculateGainTypes.WEIGHT,
            porker:
                PorkerGainOptions &&
                PorkerGainOptions === CalculateGainTypes.WEIGHT,
        };
    }, [PigletGainOptions, PorkerGainOptions]);

    const gaugesResource = useMemo(() => {
        const rows = [
            "generalMortality",
            "generalSellWeight",
            "generalSellWeightTotal",
        ];
        if (groupTypes.hasWeaners) {
            rows.push(
                "pigletMortality",
                "pigletSellWeight",
                "pigletSellWeightTotal"
            );
            if (isWeightGain.piglet) {
                rows.push(
                    "pigletGain",
                    "pigletLastWeighting",
                    "pigletFirstWeighting"
                );
            } else {
                rows.push(
                    "pigletSellGain",
                    "pigletSellDates",
                    "pigletFirstWeighting"
                );
            }
        }
        if (groupTypes.hasGilts) {
            rows.push("giltMortality", "giltSellWeight", "giltSellWeightTotal");
        }
        if (groupTypes.hasFinisher) {
            rows.push(
                "porkerMortality",
                "porkerSellWeight",
                "porkerSellWeightTotal"
            );
            if (isWeightGain.porker) {
                rows.push(
                    "porkerGain",
                    "porkerLastWeighting",
                    "porkerFirstWeighting"
                );
            } else {
                rows.push(
                    "porkerSellGain",
                    "porkerSellDates",
                    "porkerFirstWeighting"
                );
            }
        }
        if (!isWeightGain.piglet && !isWeightGain.porker) {
            rows.push("generalSellGain");
        }
        return fetchData(
            getResults(
                undefined,
                rows,
                "porker",
                [group.AnmGrp],
                undefined,
                undefined,
                "results"
            )
        );
    }, [groupTypes, group.AnmGrp, isWeightGain]);

    return (
        <div className="position-relative">
            <Suspense fallback={<GroupGaugesLoader />}>
                <CollapsableContainer.Card
                    header={t("results.results")}
                    defaultExpanded>
                    <GroupGaugesRendererRow
                        forageLocations={forageLocations}
                        group={group}
                        groupInfo={groupInfo}
                        groupTypes={groupTypes}
                        inserted={inserted}
                        waterLocations={waterLocations}
                        resource={gaugesResource}
                        loading={loading}
                    />
                </CollapsableContainer.Card>
            </Suspense>
            <EconomyData
                groupInfo={groupInfo}
                forageLocations={forageLocations}
                waterLocations={waterLocations}
                groupResults={group}
                inserted={inserted}
            />
        </div>
    );
}

function GroupGaugesSuspense({
    group,
    groupInfo,
    forageLocations,
    waterLocations,
    loading,
}) {
    // const queries = useMemo(() => {
    //     const valueKeys = [{key: group.AnmGrp}];
    //     const query = getRowQuery("porker", "generalInserted", valueKeys);
    //     return [
    //         {
    //             SQL: GROUP_ANIMAL_TYPES,
    //             ClientQueryID: "groupAnimalTypes",
    //             Parameters: [group.AnmGrp],
    //         },
    //         {
    //             SQL: query,
    //             ClientQueryID: "insertedQuery",
    //         },
    //     ];
    // }, [group.AnmGrp]);

    const resource = useMemo(() => {
        async function getData() {
            const promises = [
                getResults(
                    undefined,
                    ["generalInserted"],
                    "porker",
                    [group.AnmGrp],
                    undefined,
                    undefined,
                    "results"
                ),
                getQueryResults({
                    Queries: [
                        {
                            SQL: GROUP_ANIMAL_TYPES,
                            ClientQueryID: "groupAnimalTypes",
                            Parameters: [group.AnmGrp],
                        },
                    ],
                }),
            ];
            const res = await Promise.allSettled(promises);
            console.error(res);
            let tmp = {
                generalInserted: res[0].value?.generalInserted,
                groupAnimalTypes: res[1].value?.QueriesResults?.[0],
            };
            return tmp;
        }

        return fetchData(getData());
    }, [group.AnmGrp]);

    return (
        <Suspense fallback={<GroupGaugesLoader />}>
            <GroupGaugesRenderer
                resource={resource}
                group={group}
                groupInfo={groupInfo}
                forageLocations={forageLocations}
                waterLocations={waterLocations}
                loading={loading}
            />
        </Suspense>
    );
}

class GroupGaugesErrorBoundary extends Component {
    state = {
        hasError: false,
    };

    static getDerivedStateFromError(error) {
        return {hasError: true};
    }

    render() {
        const {children, t} = this.props;
        if (this.state.hasError) {
            return (
                <CollapsableContainer.Card
                    header={t("results.results")}
                    defaultExpanded>
                    <InfoBox boxColor="error" outlined>
                        <i className="fa-solid fa-exclamation-circle me-2" />
                        <span>
                            {t("newSettings.devices.settingsInfo.noData")}
                        </span>
                    </InfoBox>
                </CollapsableContainer.Card>
            );
        }
        return children;
    }
}

export default function GroupGauges({group, groupInfo}) {
    const {t} = useTranslation();

    const forageLocations = useDeviceLocations("forage", false);
    const waterLocations = useDeviceLocations("water", false);

    const loading = useSelector((state) =>
        getGroupGaugesAggDataLoading(
            state,
            groupInfo.start,
            groupInfo.end,
            waterLocations,
            forageLocations
        )
    );

    return (
        <GroupGaugesErrorBoundary t={t}>
            <GroupGaugesSuspense
                group={group}
                groupInfo={groupInfo}
                forageLocations={forageLocations}
                waterLocations={waterLocations}
                loading={loading}
            />
        </GroupGaugesErrorBoundary>
    );
}
